<template>
  <Dialog
    ref="dialogForwardCart"
    dialog-mode="medium"
    :class="['mobile-full', isMobile ? 'flyout-right' : '']"
    content-class="dialog-content"
    class="forward-dialog"
  >
    <template #title>
      <h4 class="m-0 text-base font-semibold capitalize md:text-xl">{{ $t("shared.cart.forward_cart") }}</h4>
    </template>
    <template #content>
      <p class="mb-4 font-medium leading-6">
        {{ $t("shared.cart.forward_label") }}
      </p>
      <VcButton type="button" class="tertiary h-[50px] w-full" variant="" @click="copyCartCodeToClipboard">
        <span v-if="copyStatus.length === 0" class="text-base font-semibold">
          <VcIcon name="copy-text" :size="20" class="ml-[-2px]" />
          {{ cartCode }}
        </span>
        <span v-if="copyStatus.length > 0" class="text-base font-semibold">
          <VcIcon name="check-circle-alt" :size="20" class="ml-[-2px]" />
          {{ copyStatus }}
        </span>
      </VcButton>
      <p class="mb-4 mt-6 w-full border-t pb-0 pt-6 font-medium leading-6">
        {{ $t("shared.cart.forward_text") }}
      </p>
      <button
        v-if="!emailShow"
        type="button"
        full-width
        class="hover:text-[--color-neutral-a1 flex items-center gap-1.5 whitespace-nowrap font-medium text-[--color-link]"
        @click="addForwardCartEmail"
      >
        <VcIcon name="plus-alt" :size="20" class="" />
        {{ $t("pages.cart.enter_email_address") }}
      </button>
      <div v-if="emailShow">
        <form @submit.prevent="handleSubmit">
          <VcInput
            id="email"
            v-model="email"
            type="email"
            :class="{ 'is-invalid': !isEmailValid }"
            :label="$t('shared.cart.forward_recipient_email') + '*'"
            class="mb-4 font-medium"
            :placeholder="$t('shared.cart.forward_recipient_email')"
            :error="!isEmailValid"
            :message="!isEmailValid ? $t('shared.cart.forward_valid_email') : ''"
            @input="handleFormInteraction"
          />

          <VcTextarea
            v-model="notes"
            :label="$t('shared.cart.forward_notes')"
            class="mb-4 font-medium"
            :placeholder="$t('shared.cart.forward_notes_placeholder')"
            :max-length="8000"
            :rows="4"
            counter
          />

          <VcInput
            id="senderName"
            v-model="senderName"
            :label="$t('shared.cart.forward_sender_name') + (isJapanese ? '*' : '')"
            class="mb-4 font-medium"
            :placeholder="$t('shared.cart.forward_sender_name')"
            :error="isJapanese && !isSenderNameValid"
            :message="isJapanese && !isSenderNameValid ? $t('shared.cart.forward_sender_name_required') : ''"
          />

          <VcInput
            id="senderEmail"
            v-model="senderEmail"
            type="email"
            :class="{ 'is-invalid': !isSenderEmailValid }"
            :label="$t('shared.cart.forward_sender_email') + (isJapanese ? '*' : '')"
            class="mb-4 font-medium"
            :placeholder="$t('shared.cart.forward_sender_email')"
            :error="!isSenderEmailValid"
            :message="!isSenderEmailValid ? $t('shared.cart.forward_valid_email') : ''"
          />

          <VcCheckbox v-model="sendCopy" class="mb-6">
            {{ $t("shared.cart.forward_send_copy") }}
          </VcCheckbox>

          <FormCaptcha
            ref="captchaRef"
            v-model="captchaCode"
            element-key="captchaCode"
            :site-key="globals.hCaptchaSiteKey"
            class="mb-6"
          />

          <VcButton
            type="submit"
            :disabled="
              email.length === 0 ||
              !isEmailValid ||
              !captchaCode ||
              !isSenderEmailValid ||
              (isJapanese && !isSenderNameValid)
            "
            color="primary"
            variant="solid"
            class="mt-6 h-[48px] w-full"
            size="lg"
            >{{ $t("pages.cart.forward_cart") }}</VcButton
          >
        </form>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { forwardCart } from "@/core/api/graphql/cart/queries/forwardCart";
import { globals } from "@/core/globals";
import { useUser } from "@/shared/account/composables/useUser";
import { useThorlabsGtm } from "@/shared/gtm/gtm";
import FormCaptcha from "@/shared/forms/components/form-captcha.vue";
import Dialog from "@/shared/thorlabs/shared/components/dialog-modal.vue";

const cartCode = ref("");
const captchaCode = ref("");

const { t } = useI18n();

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("md");
const isJapanese = computed(() => globals.cultureName?.startsWith("ja"));

// Define reactive state variables
const emailShow = ref(false);
const email = ref("");
const senderEmail = ref("");
const senderName = ref("");
const notes = ref("");
const sendCopy = ref(true);
const copyStatus = ref("");
const captchaRef = ref<InstanceType<typeof FormCaptcha> | null>(null);
const { user } = useUser();
const formStarted = ref(false);

const { gtmSendForwardCartEventAsync, gtmSendFormStartEvent } = useThorlabsGtm();

// Validation logic
const isEmailValid = computed(() => {
  // Basic email pattern validation
  const pattern = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
  if (email.value.length === 0) {
    return true;
  }
  return pattern.test(email.value);
});

const isSenderEmailValid = computed(() => {
  if (!sendCopy.value && !isJapanese.value) {
    return true;
  }
  const pattern = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
  if (senderEmail.value.length === 0) {
    return false;
  }
  return pattern.test(senderEmail.value);
});

const isSenderNameValid = computed(() => {
  if (!isJapanese.value) {
    return true;
  }
  return senderName.value.length > 0;
});

// Handle form submission
const handleSubmit = async () => {
  if (isEmailValid.value && isSenderEmailValid.value) {
    const forwardedCart = await forwardCart(
      cartCode.value,
      senderEmail.value,
      email.value,
      captchaCode.value,
      senderName.value || undefined,
      notes.value || undefined,
      sendCopy.value || undefined,
    );

    if (forwardedCart) {
      captchaRef.value?.reset();
      dialogForwardCart.value?.closeDialog();
      await gtmSendForwardCartEventAsync();
    }
  }
};

const addForwardCartEmail = () => {
  emailShow.value = !emailShow.value;
};

const dialogForwardCart = ref<InstanceType<typeof Dialog> | null>(null);
const showForwardCartDialog = (code: string | undefined) => {
  if (code && code.length > 0) {
    cartCode.value = code;
    senderName.value = [user.value?.contact?.firstName, user.value?.contact?.lastName].filter(Boolean).join(" ");
    senderEmail.value = user.value?.email ?? "";
    notes.value = "";
    sendCopy.value = true;
    dialogForwardCart.value?.openDialog();
  }
};

async function copyCartCodeToClipboard() {
  if (cartCode.value !== undefined && cartCode.value.length > 0) {
    await navigator.clipboard.writeText(cartCode.value);
    copyStatus.value = t("common.messages.clipboard_copy");
    setTimeout(() => {
      copyStatus.value = "";
    }, 2000);
  }
}

// GTM form tracking
const handleFormInteraction = () => {
  if (formStarted.value) {
    return;
  }

  formStarted.value = true;

  gtmSendFormStartEvent("ForwardCartForm", "ForwardCartForm", "email", "Email", "email", 1, window.location.href, 1);
};

defineExpose({
  showForwardCartDialog,
});
</script>

<style lang="scss">
.forward-dialog {
  .captcha {
    @apply mt-6;
  }
}
</style>
